import React, { useState } from 'react';
import { Container, Row, Col, Button, Modal, Form, Alert, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const Home = () => {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ login: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const open = () => {
    setShow(true);
    setError('');
    setForm({ login: '', password: '' });
  };
  const close = () => setShow(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Determine if login is email or phone
      const isEmail = form.login.includes('@');
      const loginField = isEmail ? 'email' : 'phone';
      
      // Prepare login data
      const loginData = {
        [loginField]: form.login,
        password: form.password
      };

      // Try owner -> manager -> superadmin sequentially
      const attempts = [
        { url: '/api/auth/login', tokenKey: 'restaurantToken', next: '/admin', useEmail: true },
        { url: '/api/manager/login', tokenKey: 'managerToken', next: null, useEmail: false }, // next будет определен динамически
        { url: '/api/superadmin/login', tokenKey: 'superAdminToken', next: '/superadmin', useEmail: true },
      ];

      let lastError = '';
      for (const attempt of attempts) {
        try {
          // Skip manager login if input is email, skip others if input is phone
          if (isEmail && !attempt.useEmail) continue;
          if (!isEmail && attempt.useEmail) continue;
          
          const res = await axios.post(attempt.url, loginData);
          if (res.data?.token) {
            // Clear other roles to avoid overlap
            localStorage.removeItem('restaurantToken');
            localStorage.removeItem('managerToken');
            localStorage.removeItem('superAdminToken');
            // Save token
            localStorage.setItem(attempt.tokenKey, res.data.token);
            
            // Determine redirect path for manager based on permissions
            let redirectPath = attempt.next;
            if (attempt.tokenKey === 'managerToken') {
              redirectPath = res.data?.canManageMenu ? '/admin/menu' : '/manager/orders-page';
            }
            
            close();
            navigate(redirectPath);
            return;
          }
        } catch (err) {
          lastError = err?.response?.data?.message || 'Неверные учетные данные';
        }
      }
      setError(lastError || 'Не удалось войти. Проверьте данные.');
    } catch (err) {
      setError(err.response?.data?.message || 'Ошибка входа. Проверьте email/номер телефона/пароль.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section bg-primary text-white py-5">
        <Container>
          <Row className="align-items-center min-vh-50">
            <Col lg={6} className="text-center text-lg-start">
              <h1 className="display-4 fw-bold mb-4">
                Цифровые QR-меню для вашего ресторана
              </h1>
              <p className="lead mb-4">
                Современное решение для управления меню, заказами и клиентами. 
                Создайте цифровое меню за минуты и начните принимать заказы онлайн.
              </p>
              <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center justify-content-lg-start">
                <Button size="lg" variant="light" onClick={open}>
                  <i className="bi bi-box-arrow-in-right me-2"></i>
                  Войти в систему
                </Button>
                
              </div>
            </Col>
            <Col lg={6} className="text-center mt-4 mt-lg-0">
              <div className="hero-image">
                <i className="bi bi-phone display-1 text-white-50"></i>
                <div className="mt-3">
                  <i className="bi bi-qr-code-scan display-4 text-white"></i>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Features Section */}
      <section className="features-section py-5">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-3">Почему выбирают нас?</h2>
              <p className="lead text-muted">
                Все необходимые инструменты для успешного ресторанного бизнеса
              </p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={6}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="feature-icon mb-3">
                    <i className="bi bi-qr-code display-4 text-primary"></i>
                  </div>
                  <h5 className="fw-bold">QR-меню</h5>
                  <p className="text-muted">
                    Создайте красивое цифровое меню с фотографиями блюд. 
                    Клиенты сканируют QR-код и сразу видят ваше меню.
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="feature-icon mb-3">
                    <i className="bi bi-cart-check display-4 text-success"></i>
                  </div>
                  <h5 className="fw-bold">Онлайн заказы</h5>
                  <p className="text-muted">
                    Клиенты могут заказывать блюда прямо из меню. 
                    Заказы поступают в реальном времени на ваше устройство.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How it works Section */}
      <section className="how-it-works-section bg-light py-5">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-3">Как это работает?</h2>
              <p className="lead text-muted">
                Всего 3 простых шага до запуска вашего цифрового меню
              </p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={4}>
              <div className="text-center">
                <div className="step-number bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '60px', height: '60px'}}>
                  <span className="fw-bold fs-4">1</span>
                </div>
                <h5 className="fw-bold">Регистрация</h5>
                <p className="text-muted">
                  Обратитесь к нам для регистрации вашего ресторана
                </p>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center">
                <div className="step-number bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '60px', height: '60px'}}>
                  <span className="fw-bold fs-4">2</span>
                </div>
                <h5 className="fw-bold">Создание меню</h5>
                <p className="text-muted">
                  Добавьте блюда, фотографии и цены в ваше цифровое меню
                </p>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center">
                <div className="step-number bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '60px', height: '60px'}}>
                  <span className="fw-bold fs-4">3</span>
                </div>
                <h5 className="fw-bold">QR-код</h5>
                <p className="text-muted">
                  Распечатайте QR-код и разместите на столах. Готово!
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <footer className="bg-dark text-white py-4">
        <Container>
          <Row className="align-items-center">
            <Col md={6}>
              <h5 className="mb-0">QR Menu </h5>
              <small >Современные решения для ресторанного бизнеса</small>
            </Col>
            <Col md={6} className="text-md-end">
              <small className="mb-0">
                © 2025 QR Menu Все права защищены.
              </small>
            </Col>
          </Row>
        </Container>
      </footer>

      {/* Login Modal */}
      <Modal show={show} onHide={close} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fw-semibold">Вход в систему</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Form onSubmit={onSubmit}>
            <Form.Group className="mb-3">
              <Form.Label className="small text-muted">Email или номер телефона</Form.Label>
              <Form.Control
                size="lg"
                type="text"
                name="login"
                value={form.login}
                onChange={onChange}
                placeholder="email@example.com или +375291234567"
                required
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="small text-muted">Пароль</Form.Label>
              <Form.Control
                size="lg"
                type="password"
                name="password"
                value={form.password}
                onChange={onChange}
                placeholder="Введите пароль"
                required
              />
            </Form.Group>

            <Button type="submit" variant="primary" className="w-100 py-2" disabled={loading}>
              {loading ? 'Вход...' : 'Войти'}
            </Button>
          </Form>

        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Home;